import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { validatePersonName } from '@/lib/people/personDisplayName';
import { Loader2, CheckCircle2, XCircle, Building2 } from 'lucide-react';
import {
  CURRENT_BETA_AGREEMENT_VERSION,
} from '@/legal/versions';

interface InvitationDetails {
  valid: boolean;
  expired?: boolean;
  already_used?: boolean;
  email: string;
  shop_id: string;
  shop_name: string;
  role: string;
  user_exists: boolean;
  error?: string;
}

const JoinTeam = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'email_confirm'>('loading');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [password, setPassword] = useState('');
  // The person's name, captured where the account is made — otherwise signup
  // stores the email as their display name and the shop sees an address.
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No invitation token provided.');
      return;
    }

    const fetchDetails = async () => {
      try {
        // Check if user already has a session — if so, skip to accept
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session) {
          // User is already logged in — go directly to accept step
          navigate(`/join-team/accept?token=${token}`, { replace: true });
          return;
        }

        const { data, error } = await supabase.functions.invoke('get-team-invitation-details', {
          body: { token },
        });

        if (error) throw error;

        if (!data?.valid) {
          setStatus('error');
          if (data?.expired) {
            setErrorMessage('This invitation has expired. Please ask the team manager to send a new one.');
          } else if (data?.already_used) {
            setErrorMessage('This invitation has already been accepted.');
          } else if (data?.error === 'canceled') {
            setErrorMessage('This invitation has been canceled.');
          } else {
            setErrorMessage('This invitation link is invalid.');
          }
          return;
        }

        setInvitation(data as InvitationDetails);
        setStatus('ready');
      } catch (err: any) {
        console.error('Failed to fetch invitation details:', err);
        setStatus('error');
        setErrorMessage('Failed to load invitation details. Please try again.');
      }
    };

    fetchDetails();
  }, [token, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation || !token) return;
    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password,
      });

      if (error) {
        toast({
          title: 'Sign In Failed',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      toast({ title: 'Signed in!', description: 'Continuing to join the team...' });
      navigate(`/join-team/accept?token=${token}`, { replace: true });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation || !token) return;

    const nameError = validatePersonName(fullName, invitation.email);
    if (nameError) {
      toast({ title: 'Your name', description: nameError, variant: 'destructive' });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: 'Passwords do not match', description: 'Please ensure both passwords are identical.', variant: 'destructive' });
      return;
    }
    if (password.length < 8) {
      toast({ title: 'Password too short', description: 'Password must be at least 8 characters.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);

    // Store token BEFORE signup so it survives regardless of what happens next
    localStorage.setItem('joinTeamToken', token);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm?invitation=${token}`,
          data: {
            account_type: 'shop',
            // handle_new_user stores coalesce(display_name, email): a typed
            // name is what the shop sees, never the address.
            display_name: fullName.trim(),
          },
        },
      });

      if (error) {
        if (error.message.includes('already registered') || error.message.includes('User already registered')) {
          // Ghost user from a previous attempt — switch to sign-in mode.
          // Keep joinTeamToken so the post-sign-in redirect to
          // /join-team/accept still has the token to consume.
          toast({ title: 'Account already exists', description: 'Switching to sign-in. Please enter your password.', variant: 'default' });
          setInvitation(prev => prev ? { ...prev, user_exists: true } : prev);
          setPassword('');
        } else {
          // FIX-R130-LOW-1: hard signup failure (not "already registered").
          // Drop the joinTeamToken we proactively wrote at the top of this
          // handler so a stale token doesn't poison a later, unrelated
          // signup or password-reset return on this device.
          try { localStorage.removeItem('joinTeamToken'); } catch { /* best-effort; ignore */ }
          toast({ title: 'Sign Up Failed', description: error.message, variant: 'destructive' });
        }
        return;
      }

      // Record beta ack
      const userId = data.user?.id;
      if (userId) {
        const ua = navigator.userAgent || 'unknown';
        await supabase
          .from('legal_acceptances')
          .upsert(
            [{ user_id: userId, agreement_type: 'beta_agreement_ack', agreement_version: CURRENT_BETA_AGREEMENT_VERSION, user_agent: ua }],
            { onConflict: 'user_id,agreement_type,agreement_version', ignoreDuplicates: true }
          );
      }

      // Check if session was returned immediately (auto-confirm enabled)
      if (data.session) {
        toast({ title: 'Account created!', description: 'Continuing to join the team...' });
        navigate(`/join-team/accept?token=${token}`, { replace: true });
      } else {
        // Email confirmation required — store token for PKCE return path
        localStorage.setItem('joinTeamToken', token);
        setStatus('email_confirm');
        toast({ title: 'Account created!', description: 'Check your email to verify your account.' });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatRole = (role: string) => {
    return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Loading
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading invitation...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email confirmation pending
  if (status === 'email_confirm') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle2 className="h-10 w-10 mx-auto text-primary mb-2" />
            <CardTitle>Check Your Email</CardTitle>
            <CardDescription>
              We've sent a verification link to <strong>{invitation?.email}</strong>.
              Click the link in your email to verify your account and join{' '}
              <strong>{invitation?.shop_name}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground text-center">
              Didn't receive the email? Check your spam folder or try signing up again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-10 w-10 mx-auto text-destructive mb-2" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ready — show sign-in or sign-up form
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Building2 className="h-10 w-10 mx-auto text-primary mb-2" />
          <CardTitle>Welcome to OptiBay</CardTitle>
          <CardDescription>
            You've been invited to join{' '}
            <strong>{invitation?.shop_name}</strong> as{' '}
            <strong>{formatRole(invitation?.role || '')}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Email display (read-only) */}
          <div className="space-y-2 mb-6">
            <Label>Email</Label>
            <Input
              value={invitation?.email || ''}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              This email was set by your team invitation
            </p>
          </div>

          {invitation?.user_exists ? (
            /* Sign-in form for existing users */
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</> : 'Sign In & Join Team'}
              </Button>
            </form>
          ) : (
            /* Create account form for new users */
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full-name">Your name</Label>
                <Input
                  id="full-name"
                  data-testid="join-full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  autoComplete="name"
                  placeholder="First and last name"
                />
                <p className="text-xs text-muted-foreground">How your team will see you.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Create Password</Label>
                <PasswordInput
                  id="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <PasswordInput
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating account...</> : 'Create Account & Join Team'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinTeam;
